import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import SiteBrandRow from "@/components/SiteBrandRow";
import AuthMobileTopBar from "@/components/auth/AuthMobileTopBar";
import BackendServiceNotice from "@/components/BackendServiceNotice";
import AuthFormFieldMessage from "@/components/auth/AuthFormFieldMessage";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { getLoginFieldErrors, hasFieldErrors } from "@/lib/authFormValidation";
import { authInputClassName, useAuthFormValidation } from "@/hooks/useAuthFormValidation";

const LOGIN_FIELDS = ["email", "password"];

export default function CustomerLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { touch, markSubmitted, shouldShow } = useAuthFormValidation(LOGIN_FIELDS);

  const errors = useMemo(
    () => getLoginFieldErrors({ email, password }),
    [email, password],
  );

  const submit = async (e) => {
    e.preventDefault();
    markSubmitted();
    if (hasFieldErrors(errors)) return;

    setLoading(true);
    try {
      await login({ kind: "customer", email: email.trim().toLowerCase(), password });
      toast.success("로그인 성공");
      navigate("/mypage", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, "로그인에 실패했습니다."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="login-page" className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
      <AuthMobileTopBar
        backTestId="login-back-button"
        linkTestId="login-logo-link"
        logoTestId="login-logo-image"
      />
      <div className="absolute inset-0 bg-grid radial-mask opacity-50 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <SiteBrandRow
          linkTo="/"
          size="lg"
          centered
          iconOnly
          className="mb-10 auth-page-brand-desktop"
          linkTestId="login-logo-link-desktop"
          logoTestId="login-logo-image-desktop"
        />
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-8">
          <BackendServiceNotice className="mb-5" compact />
          <h1 className="font-display text-2xl font-medium mb-2">로그인</h1>
          <p className="text-sm text-white/50 mb-6">마이페이지 접근을 위해 로그인하세요.</p>
          <form onSubmit={submit} className="space-y-3" noValidate>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 block">이메일</label>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  touch("email");
                  setEmail(e.target.value);
                }}
                onBlur={() => touch("email")}
                autoComplete="email"
                data-testid="login-email"
                aria-invalid={shouldShow("email") && errors.email ? "true" : undefined}
                className={authInputClassName(shouldShow("email") && errors.email)}
                placeholder="이메일을 입력하세요"
              />
              <AuthFormFieldMessage message={shouldShow("email") ? errors.email : ""} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1.5 block">비밀번호</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  touch("password");
                  setPassword(e.target.value);
                }}
                onBlur={() => touch("password")}
                autoComplete="current-password"
                data-testid="login-password"
                aria-invalid={shouldShow("password") && errors.password ? "true" : undefined}
                className={authInputClassName(shouldShow("password") && errors.password)}
                placeholder="비밀번호를 입력하세요"
              />
              <AuthFormFieldMessage message={shouldShow("password") ? errors.password : ""} />
            </div>
            <button
              disabled={loading}
              data-testid="login-submit"
              className="btn-primary w-full justify-center mt-4 disabled:opacity-50"
            >
              {loading ? "로그인 중..." : "로그인"}
            </button>
          </form>
          <p className="text-xs text-white/40 mt-5 text-center">
            계정이 없으신가요? <Link to="/signup" className="text-white underline">회원가입</Link>
          </p>
          <p className="text-xs text-white/35 mt-3 text-center">
            관리자이신가요?{" "}
            <Link to="/admin/login" className="text-white/70 underline hover:text-white">
              관리자 로그인
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}
